import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, Stack, Alert } from '@mui/material';
import MonacoEditor from '../../components/MonacoEditor';

// Debounce function to delay updates while typing
function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, ms);
  };
}

function GeoJsonEditorPanel({ onGeoJsonChange, currentGeoJson, triggerResize }) {
  // This state is ONLY for the editor's visible content.
  const [editorContent, setEditorContent] = useState('');
  const [error, setError] = useState('');
  const editorRef = useRef(null);
  const isUserTyping = useRef(false);

  // Effect to trigger layout on demand from parent
  useEffect(() => {
    if (triggerResize && editorRef.current) {
      // A small delay ensures the container has finished resizing
      setTimeout(() => editorRef.current.layout(), 10);
    }
  }, [triggerResize]);

  // **THE DEFINITIVE FIX: PART 1 - SYNCHRONIZING MAP TO EDITOR**
  // This effect listens for external changes from the map (the prop).
  // It updates the editor's content ONLY when the external data is different,
  // and crucially, it will not interrupt the user while they are typing.
  useEffect(() => {
    if (isUserTyping.current) return; // Do not update if user is typing
    try {
      const formattedProp = JSON.stringify(currentGeoJson, null, 2);
      setEditorContent(formattedProp);
    } catch (e) {
      setEditorContent("Error formatting incoming GeoJSON.");
    }
  }, [currentGeoJson]);

  // **THE DEFINITIVE FIX: PART 2 - SYNCHRONIZING EDITOR TO MAP**
  // This debounced function sends valid JSON to the parent (and the map).
  const debouncedUpdate = useRef(debounce((value) => {
    try {
      const parsed = JSON.parse(value);
      onGeoJsonChange(parsed);
      setError('');
    } catch (e) {
      setError('Invalid JSON format.');
    } finally {
      isUserTyping.current = false;
    }
  }, 500)).current;

  const handleEditorChange = (value) => {
    isUserTyping.current = true;
    setEditorContent(value); // Update the editor's state immediately for smooth typing.
    debouncedUpdate(value); // Schedule the map to be updated.
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(editorContent);
      const formatted = JSON.stringify(parsed, null, 2);
      setEditorContent(formatted);
      onGeoJsonChange(parsed); // Also update the map with the formatted version
      setError('');
    } catch (e) {
      setError('Cannot format invalid JSON.');
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 1, flexShrink: 0 }}>GeoJSON Editor</Typography>
      
      <Box sx={{ flexGrow: 1, border: '1px solid #ccc', borderRadius: 1, minHeight: 0 }}>
        <MonacoEditor
          ref={editorRef}
          language="json"
          value={editorContent}
          onChange={handleEditorChange}
        />
      </Box>
      
      <Stack direction="row" spacing={1} sx={{ mt: 2, flexShrink: 0 }}>
        <Button onClick={handleFormat} variant="outlined">Format JSON</Button>
      </Stack>
      {error && <Alert severity="warning" sx={{ mt: 1, flexShrink: 0 }}>{error}</Alert>}
    </Box>
  );
}

export default GeoJsonEditorPanel;
